import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { PageBody } from '@/components/AppShell'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from '@/components/ui/empty'
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { adminApi } from '@/lib/api'
import { copyText } from '@/lib/utils'

/** Generating and watching the links that go out to reviewers. */
export function LinksPage() {
  const client = useQueryClient()
  const datasets = useQuery({ queryKey: ['admin-datasets'], queryFn: adminApi.datasets })
  const reviewers = useQuery({ queryKey: ['admin-reviewers'], queryFn: adminApi.reviewers })
  const invites = useQuery({ queryKey: ['admin-invites'], queryFn: adminApi.invites })
  const [slug, setSlug] = useState('')
  const [reviewerId, setReviewerId] = useState('')

  const create = useMutation({
    mutationFn: () => adminApi.createInvite(slug, reviewerId),
    onSuccess: (result) => {
      toast.success(result.reused ? 'Ten link już istniał' : 'Link wygenerowany')
      void client.invalidateQueries({ queryKey: ['admin-invites'] })
    },
    onError: (error) =>
      toast.error('Nie udało się wygenerować', {
        description: error instanceof Error ? error.message : undefined,
      }),
  })

  const act = useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'revoke' | 'reopen' }) =>
      action === 'revoke' ? adminApi.revokeInvite(id) : adminApi.reopenInvite(id),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['admin-invites'] }),
  })

  // One entry per report; the newest version wins when a link is issued.
  const slugs = [...new Set((datasets.data ?? []).map((dataset) => dataset.slug))]
  const rows = invites.data ?? []

  return (
    <PageBody className="flex flex-col gap-5">
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Wygeneruj link</CardTitle>
        </CardHeader>
        <CardContent>
          <form
            className="flex flex-wrap items-end gap-4"
            onSubmit={(event) => {
              event.preventDefault()
              if (slug && reviewerId) create.mutate()
            }}
          >
            <FieldGroup className="w-56 gap-2">
              <Field>
                <FieldLabel htmlFor="dataset">Zbiór</FieldLabel>
                <Select value={slug} onValueChange={setSlug}>
                  <SelectTrigger id="dataset">
                    <SelectValue placeholder="wybierz…" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {slugs.map((item) => (
                        <SelectItem key={item} value={item}>
                          {item}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </Field>
            </FieldGroup>
            <FieldGroup className="w-56 gap-2">
              <Field>
                <FieldLabel htmlFor="reviewer">Recenzent</FieldLabel>
                <Select value={reviewerId} onValueChange={setReviewerId}>
                  <SelectTrigger id="reviewer">
                    <SelectValue placeholder="wybierz…" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {(reviewers.data ?? []).map((reviewer) => (
                        <SelectItem key={reviewer.id} value={reviewer.id}>
                          {reviewer.nick}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </Field>
            </FieldGroup>
            <Button type="submit" disabled={!slug || !reviewerId || create.isPending}>
              Generuj
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Wysłane linki</CardTitle>
        </CardHeader>
        <CardContent>
          {rows.length === 0 ? (
            <Empty>
              <EmptyHeader>
                <EmptyTitle>Żaden link nie został jeszcze wygenerowany</EmptyTitle>
                <EmptyDescription>
                  Wybierz zbiór i recenzenta powyżej.
                </EmptyDescription>
              </EmptyHeader>
            </Empty>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Recenzent</TableHead>
                  <TableHead>Zbiór</TableHead>
                  <TableHead>Postęp</TableHead>
                  <TableHead>Otwarty</TableHead>
                  <TableHead>Ostatnio</TableHead>
                  <TableHead>Link</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((invite) => {
                  const url = `${window.location.origin}/r/${invite.token}`
                  return (
                    <TableRow key={invite.id}>
                      <TableCell className="font-medium">{invite.nick}</TableCell>
                      <TableCell>
                        {invite.datasetLabel}{' '}
                        <span className="text-muted-foreground">
                          {invite.datasetSlug} v{invite.datasetVersion}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="flex items-center gap-2 tabular-nums">
                          {invite.answered} / {invite.totalBoards}
                          {invite.submittedAt ? <Badge tone="green">wysłane</Badge> : null}
                          {invite.revokedAt ? <Badge tone="clay">wycofany</Badge> : null}
                        </span>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {invite.openedAt ?? '—'}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {invite.lastSeenAt ?? '—'}
                      </TableCell>
                      <TableCell>
                        <span className="flex items-center gap-2">
                          {/* Always visible and selectable: when the clipboard
                              is out of reach, the link still has to be gettable. */}
                          <Input
                            readOnly
                            value={url}
                            onFocus={(event) => event.target.select()}
                            onClick={(event) => event.currentTarget.select()}
                            className="h-8 w-52 font-mono text-[11px]"
                          />
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={async () => {
                              if (await copyText(url)) {
                                toast.success('Skopiowano link')
                              } else {
                                toast.error('Przeglądarka nie dała skopiować', {
                                  description:
                                    'Zaznacz link w polu obok i skopiuj ręcznie (Ctrl/Cmd+C).',
                                })
                              }
                            }}
                          >
                            Kopiuj
                          </Button>
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        {invite.submittedAt ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => act.mutate({ id: invite.id, action: 'reopen' })}
                          >
                            Otwórz ponownie
                          </Button>
                        ) : null}
                        {!invite.revokedAt ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => act.mutate({ id: invite.id, action: 'revoke' })}
                          >
                            Wycofaj
                          </Button>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </PageBody>
  )
}
